import posthog from 'posthog-js';

// PostHog project API key (phc_…) — public by design, safe in the client bundle. In production,
// requests go through a first-party path (Vercel rewrites /px/* → PostHog's EU hosts) so ad
// blockers, which match "posthog" in URLs, don't drop events. In dev, hit PostHog directly.
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = import.meta.env.PROD
  ? `${window.location.origin}/px`
  : 'https://eu.i.posthog.com';

let initialized = false;

// Shared with the CookieConsent banner (its storage key + JSON shape).
const CONSENT_KEY = 'bricos-cookie-consent';

// Re-apply a previously-stored consent decision on init, so returning users who already accepted
// analytics are captured again (the banner only opts in on click, not on reload). With
// opt_out_capturing_by_default, no capture happens until this — or the banner — opts in.
function applyStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return; // no decision yet → stay opted out until the banner resolves
    const consent = JSON.parse(raw);
    if (consent?.analytics) posthog.opt_in_capturing();
    else posthog.opt_out_capturing();
  } catch {
    // ignore malformed consent
  }
}

export const analytics = {
  init: () => {
    if (initialized || !POSTHOG_KEY) {
      return;
    }

    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      ui_host: 'https://eu.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      disable_surveys: true,
      // Session replay ON but fully masked (GDPR); only runs after the user consents (opt-out default).
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
      },
      // Capture nothing until the user grants consent (GDPR / ePrivacy).
      opt_out_capturing_by_default: true,
      advanced_disable_toolbar_metrics: true,
      // /decide is enabled (proxied via /px) so feature flags + dynamic config work.
    });

    initialized = true;
    applyStoredConsent();
    console.log('Analytics initialized (proxied)');
  },

  identify: (userId: string, properties?: Record<string, unknown>) => {
    if (!initialized) return;
    posthog.identify(userId, properties);
  },

  reset: () => {
    if (!initialized) return;
    posthog.reset();
  },

  track: (event: string, properties?: Record<string, unknown>) => {
    if (!initialized) return;
    posthog.capture(event, properties);
  },

  // ── Segmentation: non-PII person properties (role, city, verified…) — never email/PII ──────
  setPersonProperties: (properties: Record<string, unknown>) => {
    if (!initialized) return;
    posthog.setPersonProperties(properties);
  },

  // ── Feature flags: gradual rollouts, kill switches, A/B tests ───────────
  isFeatureEnabled: (flag: string): boolean => {
    if (!initialized) return false;
    return posthog.isFeatureEnabled(flag) ?? false;
  },

  getFeatureFlag: (flag: string): string | boolean | undefined => {
    if (!initialized) return undefined;
    return posthog.getFeatureFlag(flag);
  },

  onFeatureFlags: (cb: () => void) => {
    if (!initialized) return;
    posthog.onFeatureFlags(cb);
  },

  // Booking events
  bookingStarted: (serviceType: string, isEmergency: boolean) => {
    analytics.track('booking_started', { service_type: serviceType, is_emergency: isEmergency });
  },

  bookingConfirmed: (bookingId: string, serviceType: string, amount: number) => {
    analytics.track('booking_confirmed', { booking_id: bookingId, service_type: serviceType, amount });
  },

  bookingCancelled: (bookingId: string, reason?: string) => {
    analytics.track('booking_cancelled', { booking_id: bookingId, reason });
  },

  // Payment events
  paymentInitiated: (bookingId: string, amount: number, method?: string) => {
    analytics.track('payment_initiated', { booking_id: bookingId, amount, method });
  },

  paymentCompleted: (bookingId: string, amount: number) => {
    analytics.track('payment_completed', { booking_id: bookingId, amount });
  },

  paymentFailed: (bookingId: string, error?: string) => {
    analytics.track('payment_failed', { booking_id: bookingId, error });
  },

  // Technician events
  technicianAssigned: (bookingId: string, technicianId: string, eta?: number) => {
    analytics.track('technician_assigned', { booking_id: bookingId, technician_id: technicianId, eta_minutes: eta });
  },

  jobCompleted: (bookingId: string, duration?: number) => {
    analytics.track('job_completed', { booking_id: bookingId, duration_minutes: duration });
  },

  // User events
  userSignedUp: (role: string) => {
    analytics.track('user_signed_up', { role });
  },

  userLoggedIn: (method?: string) => {
    analytics.track('user_logged_in', { method });
  },

  userLoggedOut: () => {
    analytics.track('user_logged_out');
  },

  // Property manager events
  propertyAdded: (propertyId: string, unitCount?: number) => {
    analytics.track('property_added', { property_id: propertyId, unit_count: unitCount });
  },

  tenantNotificationSent: (bookingId: string) => {
    analytics.track('tenant_notification_sent', { booking_id: bookingId });
  },

  // Dispute events
  disputeOpened: (bookingId: string) => {
    analytics.track('dispute_opened', { booking_id: bookingId });
  },

  disputeResolved: (bookingId: string, resolution?: string) => {
    analytics.track('dispute_resolved', { booking_id: bookingId, resolution });
  },

  // Loyalty events
  pointsEarned: (points: number, source: string) => {
    analytics.track('points_earned', { points, source });
  },

  pointsRedeemed: (points: number, reward: string) => {
    analytics.track('points_redeemed', { points, reward });
  },

  // Support events
  supportRequested: (type: string) => {
    analytics.track('support_requested', { type });
  },

  // Plan events
  planViewed: (planType: string) => {
    analytics.track('plan_viewed', { plan_type: planType });
  },

  planSubscribed: (planType: string) => {
    analytics.track('plan_subscribed', { plan_type: planType });
  },
};
